import {
  DEFAULT_PAGE_SIZE,
  RUN_TRIGGER,
  formatParisDateTime,
  statusLabel,
  type RunTrigger,
} from '@flux/shared';
import type { JSX } from 'react';
import { BackLink, type PageBackLink } from '@/components/layout/page.js';
import { DescriptionItem } from '@/components/ui/description-list.js';
import { Skeleton } from '@/components/ui/skeleton.js';
import { cn } from '@/lib/cn.js';

/**
 * Retour vers l'onglet Runs du Journal, avec les mêmes paramètres que la
 * liste par défaut — partagé par les deux détails de run et la page des
 * lignes en erreur.
 */
export const RUNS_BACK_LINK: PageBackLink = {
  label: 'Retour aux runs',
  to: '/journal',
  search: { view: 'runs', page: 1, pageSize: DEFAULT_PAGE_SIZE, sortDir: 'desc' },
};

export interface RunTimingItemsProps {
  startedAt: string;
  finishedAt: string | null;
  trigger: RunTrigger;
}

/** Début, fin et déclenchement d'un run — premières lignes du résumé. */
export function RunTimingItems({
  startedAt,
  finishedAt,
  trigger,
}: RunTimingItemsProps): JSX.Element {
  return (
    <>
      <DescriptionItem label="Début">
        <span className="whitespace-nowrap tabular-nums">
          {formatParisDateTime(new Date(startedAt))}
        </span>
      </DescriptionItem>
      <DescriptionItem label="Fin">
        {finishedAt ? (
          <span className="whitespace-nowrap tabular-nums">
            {formatParisDateTime(new Date(finishedAt))}
          </span>
        ) : (
          <span className="text-muted-foreground">—</span>
        )}
      </DescriptionItem>
      <DescriptionItem label="Déclenchement">{statusLabel(RUN_TRIGGER, trigger)}</DescriptionItem>
    </>
  );
}

/** Compteur du résumé : rouge seulement quand il signale des erreurs. */
export function RunCount({
  value,
  alert = false,
}: {
  value: number;
  alert?: boolean;
}): JSX.Element {
  return (
    <span
      className={cn(
        'tabular-nums',
        alert && value > 0 ? 'font-medium text-status-red-fg' : undefined,
      )}
    >
      {value}
    </span>
  );
}

/** Squelette d'un détail de run : en-tête, résumé, puis un panneau par tableau. */
export function RunDetailSkeleton({ tables }: { tables: number }): JSX.Element {
  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-3">
        <BackLink backLink={RUNS_BACK_LINK} />
        <Skeleton className="h-8 w-72 max-w-full" />
        <Skeleton className="h-4 w-32" />
      </div>
      <div className="flex flex-col gap-3 rounded-lg border border-border bg-card p-5">
        {Array.from({ length: 4 }, (_, index) => (
          <div key={`summary-${String(index)}`} className="flex items-center gap-6">
            <Skeleton className="h-3.5 w-24 shrink-0" />
            <Skeleton className="h-3.5 w-40" />
          </div>
        ))}
      </div>
      {Array.from({ length: tables }, (_, table) => (
        <div key={`table-${String(table)}`} className="rounded-lg border border-border bg-card">
          <div className="border-b border-border px-5 pb-3 pt-4">
            <Skeleton className="h-5 w-40" />
          </div>
          <div className="divide-y divide-border">
            {Array.from({ length: 3 }, (_, row) => (
              <div key={`row-${String(row)}`} className="flex items-center gap-4 px-4 py-3.5">
                <Skeleton className="h-3.5 w-full max-w-40" />
                <Skeleton className="h-3.5 w-full max-w-24" />
                <Skeleton className="h-3.5 w-full max-w-32" />
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
